<template>
  <div data-qa="game-assets-upload-section">
    <p class="upload-assets-title" data-qa="game-assets-upload-title">
      {{ $t('Gamification_Assets_Upload') }}
    </p>
    <p
      class="upload-assets-description"
      data-qa="game-assets-upload-description"
    >
      {{ $t('Gamification_Assets_Upload_Description') }}
    </p>
    <el-upload
      class="dropzone"
      :class="{ 'bottom-border-radius': files.length }"
      ref="assetsUpload"
      drag
      :show-file-list="false"
      action="#"
      :on-change="handleChange"
      multiple
      :auto-upload="false"
      data-qa="game-assets-upload-dropzone"
    >
      <div
        v-if="!files.length"
        class="empty-files-wrapper"
        data-qa="game-assets-upload-empty-state"
      >
        <icon glyph="image-placeholder" class="empty-files-icon" />
        <p class="empty-files-main" data-qa="game-assets-upload-main-text">
          {{ $t('Gamification_Assets_Upload_Drag_Drop') }}
          <span
            class="empty-files-link"
            data-qa="game-assets-upload-browse-link"
          >
            {{ $t('Gamification_Assets_Upload_Browse_Files') }}
          </span>
        </p>
        <p class="empty-files-text match-text">
          {{ $t('Gamification_Assets_Upload_File_Names_Match') }}
        </p>
      </div>
      <div
        v-else
        class="files-list-wrapper"
        data-qa="game-assets-upload-files-list"
      >
        <div
          v-for="(file, index) in files"
          :key="file.name"
          class="file-item"
          :class="{ 'invalid-file': !getFileStatus(file).valid }"
          :data-qa="`game-assets-upload-file-item-${index}`"
          @click.stop
        >
          <div class="file-content" data-qa="game-assets-upload-file-content">
            <div class="text-wraper">
              <p class="title" data-qa="game-assets-upload-file-name">
                {{ file.name }}
              </p>
              <p class="description" data-qa="game-assets-upload-file-size">
                {{ formatSize(file.size) }}
              </p>
            </div>
            <div
              v-if="!getFileStatus(file).valid"
              class="text-wraper"
              data-qa="game-assets-upload-file-error"
            >
              <p class="title">{{ getFileStatus(file).message }}</p>
              <p class="description">{{ getFileStatus(file).description }}</p>
            </div>
          </div>
          <icon
            v-show="!isLoading"
            glyph="cross"
            class="remove-file-icon"
            @click.stop="removeFile(index)"
            data-qa="game-assets-upload-remove-file-btn"
          />
        </div>
      </div>
    </el-upload>
    <div v-if="files.length" class="upload-button-wrapper">
      <el-button
        type="primary"
        :disabled="isUploadDisabled"
        @click="uploadAssets"
        data-qa="game-assets-upload-btn"
      >
        {{ $t('Gamification_Assets_Upload') }}
      </el-button>
    </div>
    <el-dialog
      :visible="isLoading"
      center
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <div class="uploading-dialog">
        <img src="/img/frameloader.gif" width="48" />
        <div>
          <h2>{{ $t('Gamification_Assets_Uploading') }}</h2>
        </div>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import JSZip from 'jszip'
import { GameAssetsTemplateToExtensionMap } from '@/modules/gamification/enum/GameAssetsTemplateEnum'
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'GameAssetsUploadFiles',
  props: {
    allowedFileNames: {
      type: Object,
      required: true
    },
    gameCampaignId: {
      type: Number
    },
    saveEndpoint: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      files: [],
      isDragging: false,
      isLoading: false
    }
  },
  computed: {
    isUploadDisabled() {
      return (
        this.files.some(file => !this.isValidFile(file).valid) || this.isLoading
      )
    },
    uploadEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/upload-assets`,
        config: { headers: { 'Content-Type': 'multipart/form-data' } }
      })
    }
  },
  methods: {
    async handleChange(file, fileList) {
      if (file.name.endsWith('.zip')) {
        await this.extractZip(file.raw)
      } else {
        this.files.push(file.raw)
      }
    },
    removeFile(index) {
      this.files.splice(index, 1)
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + sizes[i]
    },
    async extractZip(zipFile) {
      try {
        const zip = new JSZip()
        const contents = await zip.loadAsync(zipFile)

        for (const [filename, file] of Object.entries(contents.files)) {
          if (!file.dir) {
            const blob = await file.async('blob')
            const singleFile = new File([blob], filename, {
              type: this.getMimeType(filename)
            })

            this.files.push(singleFile)
          }
        }
      } catch (error) {
        console.error(error)
      }
    },
    getMimeType(filename) {
      const ext = filename
        .split('.')
        .pop()
        .toLowerCase()
      const types = {
        png: 'image/png',
        jpg: 'image/jpg',
        jpeg: 'image/jpeg',
        svg: 'image/svg+xml',
        webp: 'image/webp',
        gif: 'image/gif',

        ttf: 'font/ttf',
        otf: 'font/otf',
        woff: 'font/woff',

        mp3: 'audio/mpeg',
        wav: 'audio/wav'
      }

      return types[ext] || 'application/octet-stream'
    },
    isValidFile(file) {
      const name = file.name.slice(0, file.name.lastIndexOf('.')) || file.name

      const allFileNames = Object.values(this.allowedFileNames).flat()

      if (!allFileNames.includes(name)) {
        return {
          valid: false,
          message: this.$t('Gamification_Assets_Upload_Invalid_Name'),
          description: this.$t('Gamification_Assets_Upload_File_Names_Match')
        }
      }

      const type = Object.keys(this.allowedFileNames).find(key =>
        this.allowedFileNames[key].includes(name)
      )
      const fileType = this.isValidFileType(file, type)
      if (!fileType.valid) {
        return fileType
      }

      const fileSize = this.isValidFileSize(file)
      if (!fileSize.valid) {
        return fileSize
      }

      if (
        this.files.findIndex(f => f.name === file.name) !==
        this.files.map(f => f.name).lastIndexOf(file.name)
      ) {
        return {
          valid: false,
          message: this.$t('Gamification_Assets_Upload_Duplicate_File_Names'),
          description: this.$t(
            'Gamification_Assets_Upload_Duplicate_File_Names_Description'
          )
        }
      }
      return { valid: true }
    },
    isValidFileSize(file) {
      const maxSizeBytes = 10 * 1024 * 1024

      if (file.size > maxSizeBytes) {
        return {
          valid: false,
          message: this.$t('Gamification_Assets_Upload_Invalid_Size'),
          description: this.$t(
            'Gamification_Assets_Upload_Invalid_Size_Description',
            { size: 10 }
          )
        }
      }

      return { valid: true }
    },
    isValidFileType(file, expectedType) {
      const ext = file.name
        .split('.')
        .pop()
        .toLowerCase()

      const allowedExtensions = GameAssetsTemplateToExtensionMap[expectedType]

      if (!allowedExtensions.includes(ext)) {
        return {
          valid: false,
          message: this.$t('Gamification_Assets_Upload_Invalid_Format'),
          description: this.$t(
            'Gamification_Assets_Upload_Invalid_Format_Description',
            { name: file.name, allowedExtensions: allowedExtensions.join(', ') }
          )
        }
      }

      return { valid: true }
    },
    getFileStatus(file) {
      if (!file._status) {
        file._status = this.isValidFile(file)
      }
      return file._status
    },
    uploadAssets() {
      this.isLoading = true
      const formData = new FormData()
      this.files.forEach(file => {
        formData.append('files[]', file)
      })
      RestApi({
        ...this.uploadEndpoint,
        ...{ data: formData }
      })
        .then(({ data }) => {
          const filesWithType = data.files.map(file => {
            let type = Object.keys(this.allowedFileNames).find(key =>
              this.allowedFileNames[key].includes(file.name)
            )
            return { ...file, type }
          })
          this.saveAssets(filesWithType)
        })
        .catch(error => {
          this.isLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    saveAssets(data) {
      RestApi(this.saveEndpoint(data))
        .then(() => {
          this.$message({
            message: this.$t('Gamification_Assets_Uploaded_Successfully'),
            type: 'success'
          })
          this.files = []
          this.$emit('afterSave')
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>
<style scoped>
.upload-assets-title {
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #4b515f;
}
.upload-assets-description {
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  color: #5c6374;
}
.dropzone {
  border: 1px solid #dbdee7;
  border-radius: 4px;
  text-align: center;
  cursor: pointer;
  margin-top: 4px;
  background-color: #f8fafc;
  max-height: 400px;
  overflow: auto;
}
/deep/ .el-upload {
  width: 100%;
}
/deep/ .el-upload-dragger {
  width: 100%;
  background: transparent;
  border: none;
  padding: 15px;
  height: unset;
}
.empty-files-wrapper {
  display: flex;
  flex-direction: column;
  gap: 7px;
  align-items: center;
}
.empty-files-icon {
  font-size: 18px;
}
.empty-files-main {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: #4b515f;
}
.empty-files-link {
  color: #1d87c9;
  cursor: pointer;
  text-decoration: underline;
}
.empty-files-text {
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  line-height: 13px;
  color: #5c6374;
}
.match-text {
  font-weight: 600;
  color: #4b515f;
  margin-top: 23px;
}
.files-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.file-item {
  display: flex;
  border: 1px solid #dbdee7;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  padding: 5px 25px;
  background-color: #fff;
}
.invalid-file {
  border-color: #fb7185;
  background-color: #fde8e9;
}
.remove-file-icon {
  color: #4b515f;
  font-size: 12px;
}
.file-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.text-wraper {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: start;
}
.text-wraper .title {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: #4b515f;
  font-weight: 600;
}
.text-wraper .description {
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  line-height: 13px;
  color: #5c6374;
}
.bottom-border-radius {
  border-radius: 4px 4px 0 0;
}
.upload-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  border: 1px solid #dbdee7;
  border-top: none;
  border-radius: 0 0 4px 4px;
  background-color: #f8fafc;
}
.uploading-dialog {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/content-assets/GameAssetsUploadFiles.vue